// Directive for amount-style fields that are rendered as `type="text"` (no
// spinner arrows, no scroll-wheel surprises, no "e"/"+"/"-" that the browser
// silently accepts) but must still behave like numbers. It rewrites the value
// as the user types or pastes, so only digits and an optional decimal part
// survive.
//
// Usage:
//   v-numeric                           -> positive amount, up to 2 decimals
//   v-numeric="{ decimals: 0 }"         -> positive whole numbers only
//   v-numeric="{ allowNegative: true }" -> keeps a leading minus sign
//
// Nothing in the invoice / quotation / expense forms can legitimately be
// negative today, so allowNegative defaults to false.

const DEFAULTS = {
  decimals: 2,
  allowNegative: false,
};

// Strips everything that cannot be part of the number. This runs on every
// keystroke, so it has to tolerate half-typed values like "12." or just "-".
function sanitize(value, opts) {
  let str = String(value ?? "");
  const negative = opts.allowNegative && str.trim().startsWith("-");

  str = str.replace(/[^\d.]/g, "");

  if (opts.decimals > 0) {
    const firstDot = str.indexOf(".");
    if (firstDot !== -1) {
      // Keep the first dot, drop the rest, and cap the decimal places.
      const whole = str.slice(0, firstDot);
      const fraction = str.slice(firstDot + 1).replace(/\./g, "");
      str = whole + "." + fraction.slice(0, opts.decimals);
    }
  } else {
    str = str.replace(/\./g, "");
  }

  // Drop leading zeros ("007" -> "7") while keeping "0" and "0.5".
  str = str.replace(/^0+(?=\d)/, "");

  return negative && str !== "" ? "-" + str : str;
}

// Runs on blur: turns a half-typed value such as "12." or "-" into something
// the form can submit.
function normalize(value, opts) {
  const str = sanitize(value, opts);
  const num = parseFloat(str);
  if (isNaN(num)) return "";
  return opts.decimals > 0 ? String(num) : String(Math.trunc(num));
}

// Writes the cleaned value back and lets v-model pick it up. The re-dispatched
// event is harmless the second time around: the value is already clean, so
// applyValue bails out.
function applyValue(input, next) {
  if (input.value === next) return;
  input.value = next;
  input.dispatchEvent(new Event("input"));
}

export const numericInput = {
  mounted(el, binding) {
    const input = el.tagName === "INPUT" ? el : el.querySelector("input");
    if (!input) return;

    const opts = { ...DEFAULTS, ...(binding.value || {}) };
    const onInput = () => applyValue(input, sanitize(input.value, opts));
    const onBlur = () => applyValue(input, normalize(input.value, opts));

    input.addEventListener("input", onInput);
    input.addEventListener("blur", onBlur);
    el._numericInput = { input, onInput, onBlur };

    // Clean whatever the component prefilled the field with.
    onInput();
  },

  unmounted(el) {
    const bound = el._numericInput;
    if (!bound) return;
    bound.input.removeEventListener("input", bound.onInput);
    bound.input.removeEventListener("blur", bound.onBlur);
    delete el._numericInput;
  },
};

export default numericInput;
